import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { LoadErrorBox } from "../components/common/LoadErrorBox";
import { startCheckout, openPortal } from "../lib/billing";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useFeatures } from "../context/FeaturesContext";
import {
  BadgeCheck, Building2, ScrollText, Landmark, LineChart, ChevronRight,
  FileSpreadsheet, MessageSquareText, FileSearch, PenLine, Mic, CheckCheck,
  GraduationCap, ListChecks, FolderKanban, MessageSquare, Sparkles, ShieldCheck, LayoutGrid, X, Video,
} from "lucide-react";

// ─── Sezione 2: Verifiche e documenti ufficiali (mapping invariato) ──
const CATEGORY_META = {
  "Verifica P.IVA": {
    slug: "vies",
    icon: BadgeCheck,
    title: "Verifica VIES",
    description: "Validità Partita IVA intracomunitaria su anagrafe UE.",
  },
  "Cerca Azienda": {
    slug: "company",
    icon: Building2,
    title: "Dati azienda",
    description: "Anagrafica, soci, ATECO, fatturato da P.IVA o C.F.",
  },
  "Visure Camerali": {
    slug: "visure",
    icon: ScrollText,
    title: "Visure camerali",
    description:
      "PDF ordinari CCIAA per società di capitali, persone e individuali.",
  },
  "Bilancio Aziendale": {
    slug: "bilancio",
    icon: LineChart,
    title: "Bilancio aziendale",
    description:
      "PDF ufficiale del bilancio depositato in Camera di Commercio.",
  },
  "Visure Catastali": {
    slug: "catasto",
    icon: Landmark,
    title: "Visure catastali",
    description:
      "Elenco immobili JSON per P.IVA, C.F. o indirizzo (pre-screening).",
  },
  "Documenti Catastali Ufficiali": {
    slug: "catasto-documenti",
    icon: Landmark,
    title: "Documenti catastali ufficiali",
    description:
      "PDF firmati Agenzia delle Entrate per dossier contratti e KYC.",
  },
};
const CATEGORY_ORDER = [
  "Verifica P.IVA",
  "Cerca Azienda",
  "Visure Camerali",
  "Bilancio Aziendale",
  "Visure Catastali",
  "Documenti Catastali Ufficiali",
];

// ─── Sezione 1: Assistente AI ────────────────────────────────────────
const AI_TOOLS = [
  {
    id: "reconcile", icon: FileSpreadsheet, title: "Riconcilia due file",
    line: "Confronta due Excel e trova le differenze in secondi.",
    to: "/assistant/reconcile", flag: "ai_tools", built: true,
  },
  {
    id: "ask-file", icon: MessageSquareText, title: "Chiedi al tuo file",
    line: "Fai domande in italiano ai tuoi Excel: risposte subito, senza formule.",
    to: "/assistant/ask-file", flag: "ai_tools", built: true,
  },
  {
    id: "analyze-doc", icon: FileSearch, title: "Analizza documento",
    line: "Carica una bolletta o un contratto: i dati estratti da soli.",
    to: "/assistant/analyze-doc", flag: "ai_tools", built: true,
  },
  {
    id: "drafts", icon: PenLine, title: "Bozze e comunicazioni",
    line: "PEC, email e risposte ai clienti pronte nel tono giusto.",
    to: "/assistant/drafts", flag: "ai_tools", built: true,
  },
  {
    id: "minutes", icon: Mic, title: "Verbale riunioni",
    line: "Dalla registrazione al verbale ordinato, con decisioni e cose da fare.",
    built: false,
  },
  {
    id: "quality", icon: CheckCheck, title: "Controlli qualità dati",
    line: "Trova errori e anomalie nei tuoi elenchi prima che diventino problemi.",
    to: "/assistant/quality", flag: "ai_tools", built: true,
  },
];

// ─── Sezione 3: La tua piattaforma ───────────────────────────────────
const PLATFORM = [
  {
    id: "formazione", icon: GraduationCap, title: "Formazione",
    line: "Percorsi su misura per la tua azienda, con progressi tracciati.",
    to: "/formazione", flag: "formazione", built: true,
  },
  {
    id: "checklist", icon: ListChecks, title: "Checklist",
    line: "Liste personali e condivise.",
    to: "/checklist", flag: "checklist", built: true,
  },
  {
    id: "documenti", icon: FolderKanban, title: "Documenti",
    line: "Archivio documentale aziendale.",
    to: "/documents", flag: "documenti", built: true,
  },
  {
    id: "messaggi", icon: MessageSquare, title: "Messaggi interni",
    line: "Chat di lavoro con ricerca.",
    to: "/messages", flag: "messaggi", built: true,
  },
  {
    id: "riunioni", icon: Video, title: "Riunioni video",
    line: "Stanze del tuo team, con il tuo logo.",
    to: "/meetings", flag: "riunioni", built: true,
  },
];

// Gancio upgrade: contenuto della finestra per i moduli "Non incluso nel tuo piano"
const MODULE_TO_PLAN = { ai_tools: "business", verifica: "energia" };
const PLAN_LABEL = { business: "Business", energia: "Energia" };

const MODULE_PITCH = {
  ai_tools: {
    title: "Assistente AI",
    desc: "Strumenti AI per il lavoro d'ufficio: risultati calcolati sui tuoi file, dati mai inviati per intero al modello.",
    bullets: [
      "Riconcilia due file Excel/CSV e trova le differenze in secondi",
      "Fai domande in italiano ai tuoi file: conteggi, somme, duplicati",
      "Report scaricabili in Excel e costi trasparenti",
    ],
  },
  verifica: {
    title: "Verifiche e documenti ufficiali",
    desc: "VIES, camerali, bilanci e catasto senza uscire da Wattline.",
    bullets: [
      "Verifica P.IVA e dati azienda in un click",
      "Visure camerali e bilanci in PDF ufficiale",
      "Storico richieste condiviso con il team",
    ],
  },
  formazione: {
    title: "Formazione",
    desc: "Percorsi formativi costruiti sulle esigenze della tua azienda: ci dici cosa serve al team e prepariamo il corso per voi.",
    bullets: [
      "Corsi progettati da Wattline sulle richieste del tuo team",
      "Progressi e completamenti tracciati per persona",
      "Accesso da desktop e mobile",
    ],
  },
  checklist: {
    title: "Checklist",
    desc: "Liste personali e condivise per il lavoro di ogni giorno.",
    bullets: [
      "Liste personali e di team",
      "Attività ricorrenti",
      "Avanzamento visibile a colpo d'occhio",
    ],
  },
  messaggi: {
    title: "Messaggi interni",
    desc: "La chat di lavoro della tua azienda, con ricerca.",
    bullets: [
      "Conversazioni private tra colleghi",
      "Ricerca nei messaggi",
      "Contatore non letti in sidebar",
    ],
  },
  riunioni: {
    title: "Riunioni video",
    desc: "Stanze video del tuo team con link condivisibili anche con esterni, brandizzate col tuo logo.",
    bullets: [
      "Stanze con link univoci non indovinabili",
      "Riunioni programmate visibili in dashboard",
      "Ingresso co-branded con il logo della tua azienda",
    ],
  },
};

const SectionTitle = ({ icon: Icon, children, testId }) => (
  <h2 className="text-sm font-semibold uppercase tracking-wider text-text-secondary flex items-center gap-2" data-testid={testId}>
    <Icon size={15} strokeWidth={2} className="text-accent" /> {children}
  </h2>
);

const StatusChip = ({ status, testId }) => (
  <span className="wl-badge wl-badge--neutral flex-shrink-0" data-testid={testId}>
    {status === "soon" ? "In arrivo" : "Non incluso nel tuo piano"}
  </span>
);

// Card modulo: attiva (cliccabile), locked (flag OFF → apre il gancio upgrade) o soon
const ModuleCard = ({ icon: Icon, title, line, status, onOpen, onLocked, testId, footer }) => {
  const active = status === "active";
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-10 h-10 rounded-md bg-accent-subtle flex items-center justify-center flex-shrink-0">
          <Icon size={20} strokeWidth={1.8} className={active ? "text-accent" : "text-text-muted"} />
        </div>
        {active
          ? <ChevronRight size={16} className="text-text-muted group-hover:text-accent transition mt-2" />
          : <StatusChip status={status} testId={`${testId}-chip`} />}
      </div>
      <div className="min-w-0">
        <div className={`text-base font-semibold leading-tight ${active ? "text-text-primary" : "text-text-secondary"}`}>
          {title}
        </div>
        <div className="text-xs text-text-muted mt-1">{line}</div>
      </div>
      {footer && (
        <div className="mt-1 text-[11px] text-text-muted uppercase tracking-widest">{footer}</div>
      )}
    </>
  );
  if (active) {
    return (
      <button
        type="button" onClick={onOpen} data-testid={testId}
        className="card-neon-interactive text-left p-5 rounded-lg border border-border bg-surface group flex flex-col gap-3"
      >
        {body}
      </button>
    );
  }
  if (status === "locked" && onLocked) {
    return (
      <button
        type="button" onClick={onLocked} data-testid={testId}
        className="text-left p-5 rounded-lg border border-border bg-surface opacity-70 hover:opacity-100 hover:border-accent transition-colors flex flex-col gap-3"
      >
        {body}
      </button>
    );
  }
  return (
    <div
      data-testid={testId} aria-disabled="true"
      className="p-5 rounded-lg border border-border bg-surface opacity-70 flex flex-col gap-3 cursor-default"
    >
      {body}
    </div>
  );
};

/**
 * Catalogo piattaforma: vetrina completa di ciò che Wattline fa per il tenant.
 * 3 sezioni: Assistente AI · Verifiche e documenti ufficiali · La tua piattaforma.
 * Le card rispettano i feature flags (/me/features): attiva / "Non incluso nel
 * tuo piano" (→ gancio upgrade con richiesta attivazione) / "In arrivo".
 */
export default function ServicesCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { features } = useFeatures();
  const isSA = user?.role === "super_admin";
  const on = (flag) => isSA || !!(features && features[flag]);
  const verificaOn = on("verifica");

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requested, setRequested] = useState(new Set());
  const [pitchModule, setPitchModule] = useState(null);
  const [sendingReq, setSendingReq] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const [catalogFailed, setCatalogFailed] = useState(false);
  const [catalogReload, setCatalogReload] = useState(0);
  useEffect(() => {
    if (!verificaOn) { setServices([]); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get("/services")
      .then(({ data }) => { if (!cancelled) { setServices(Array.isArray(data) ? data : []); setCatalogFailed(false); } })
      .catch(() => { if (!cancelled) { setServices([]); setCatalogFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [verificaOn, catalogReload]);

  useEffect(() => {
    api.get("/module-requests/mine")
      .then(({ data }) => setRequested(new Set(data.map((r) => r.module))))
      .catch(() => {});
  }, []);

  const requestModule = async () => {
    setSendingReq(true);
    try {
      await api.post("/module-requests", { module: pitchModule });
      setRequested((s) => new Set([...s, pitchModule]));
      toast.success("Richiesta inviata: Wattline ti ricontatterà a breve.");
      setPitchModule(null);
    } catch (err) {
      if (err?.response?.status === 409) {
        setRequested((s) => new Set([...s, pitchModule]));
        toast.info("Richiesta già inviata: Wattline ti ricontatterà a breve.");
      } else {
        toast.error(err?.response?.data?.detail || "Invio non riuscito");
      }
    } finally {
      setSendingReq(false);
    }
  };

  const upgradePlan = async () => {
    setSendingReq(true);
    try {
      if (sub?.state === "active") await openPortal();
      else await startCheckout(MODULE_TO_PLAN[pitchModule], sub?.interval || "monthly");
    } catch { setSendingReq(false); }
  };

  const availableByCategory = useMemo(() => {
    const map = new Map();
    for (const s of services) {
      const cat = s.category;
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(s);
    }
    return map;
  }, [services]);

  const visibleCategories = useMemo(
    () => CATEGORY_ORDER.filter((cat) => (availableByCategory.get(cat) || []).length > 0),
    [availableByCategory],
  );

  const statusOf = (item) => (!item.built ? "soon" : on(item.flag) ? "active" : "locked");

  const activeCount =
    AI_TOOLS.filter((t) => t.built && on(t.flag)).length +
    (verificaOn ? visibleCategories.length : 0) +
    PLATFORM.filter((p) => p.built && on(p.flag)).length;

  const showVerifiche = verificaOn ? visibleCategories.length > 0 : true;
  const pitch = pitchModule ? (MODULE_PITCH[pitchModule] || { title: pitchModule, desc: "", bullets: [] }) : null;
  const alreadyRequested = pitchModule ? requested.has(pitchModule) : false;
  const stripePlan = pitchModule ? MODULE_TO_PLAN[pitchModule] : null;
  const selfService = Boolean(sub && sub.state !== "legacy" && user?.role === "admin" && stripePlan);

  return (
    <div className="space-y-8 max-w-6xl" data-testid="services-catalog-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-text-primary tracking-tight">
          Catalogo servizi
        </h1>
        <p className="text-sm text-text-muted mt-1" data-testid="catalog-active-count">
          {loading
            ? "Caricamento…"
            : `Tutto quello che Wattline fa per il tuo team · ${activeCount} ${
                activeCount === 1 ? "servizio attivo" : "servizi attivi"
              }.`}
        </p>
      </div>

      {/* ── Assistente AI ── */}
      <section className="space-y-4" data-testid="section-ai-tools">
        <SectionTitle icon={Sparkles} testId="section-ai-tools-title">Assistente AI</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AI_TOOLS.map((t) => (
            <ModuleCard
              key={t.id} icon={t.icon} title={t.title} line={t.line}
              status={statusOf(t)} onOpen={() => navigate(t.to)}
              onLocked={() => setPitchModule(t.flag)}
              testId={`catalog-ai-${t.id}`}
            />
          ))}
        </div>
      </section>

      {/* ── Verifiche e documenti ufficiali ── */}
      {showVerifiche && (
        <section className="space-y-4" data-testid="section-verifiche">
          <SectionTitle icon={ShieldCheck} testId="section-verifiche-title">
            Verifiche e documenti ufficiali
          </SectionTitle>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="p-5 rounded-lg border border-border bg-surface h-32 animate-pulse" data-testid="catalog-skeleton" />
              ))}
            </div>
          ) : catalogFailed && verificaOn ? (
            <div className="rounded-lg border border-border bg-surface">
              <LoadErrorBox what="il catalogo servizi" onRetry={() => setCatalogReload((k) => k + 1)} testId="services-load-error" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="catalog-grid">
              {(verificaOn ? visibleCategories : CATEGORY_ORDER).map((cat) => {
                const meta = CATEGORY_META[cat];
                const items = availableByCategory.get(cat) || [];
                return (
                  <ModuleCard
                    key={cat} icon={meta.icon} title={meta.title} line={meta.description}
                    status={verificaOn ? "active" : "locked"}
                    onOpen={() => navigate(`/search/${meta.slug}`)}
                    onLocked={() => setPitchModule("verifica")}
                    testId={`catalog-cat-${meta.slug}`}
                    footer={verificaOn
                      ? `${items.length} ${items.length === 1 ? "servizio disponibile" : "servizi disponibili"}`
                      : null}
                  />
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ── La tua piattaforma ── */}
      <section className="space-y-4" data-testid="section-piattaforma">
        <SectionTitle icon={LayoutGrid} testId="section-piattaforma-title">La tua piattaforma</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLATFORM.map((p) => (
            <ModuleCard
              key={p.id} icon={p.icon} title={p.title} line={p.line}
              status={statusOf(p)} onOpen={() => navigate(p.to)}
              onLocked={() => setPitchModule(p.flag)}
              testId={`catalog-platform-${p.id}`}
            />
          ))}
        </div>
      </section>

      {/* ── Gancio upgrade: finestra richiesta modulo ── */}
      {pitch && (
        <div className="wl-modal-overlay" onClick={() => !sendingReq && setPitchModule(null)}>
          <div
            className="wl-modal wl-modal--md" role="dialog" aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            onClick={(e) => e.stopPropagation()} data-testid="upgrade-modal"
          >
            <div className="wl-modal__header">
              <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true">
                <Sparkles size={18} />
              </div>
              <div className="wl-modal__title-block">
                <h3 id="upgrade-modal-title" className="wl-modal__title">{pitch.title}</h3>
                <p className="wl-modal__desc">Questo modulo non è incluso nel tuo piano.</p>
              </div>
              <button type="button" onClick={() => setPitchModule(null)} aria-label="Chiudi"
                      className="text-text-muted hover:text-text-primary" data-testid="upgrade-close">
                <X size={16} />
              </button>
            </div>
            <div className="wl-modal__body">
              <p className="text-sm text-text-secondary mb-3">{pitch.desc}</p>
              <ul className="space-y-2">
                {pitch.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
                    <BadgeCheck size={15} className="text-accent mt-0.5 flex-shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="wl-modal__actions">
              <button type="button" className="wl-btn wl-btn--ghost" onClick={() => setPitchModule(null)}
                      disabled={sendingReq} data-testid="upgrade-cancel">
                Annulla
              </button>
              {selfService ? (
                <button type="button" className="wl-btn wl-btn--primary"
                        disabled={sendingReq}
                        onClick={upgradePlan} data-testid="upgrade-stripe-btn">
                  {sendingReq ? "Apertura…"
                    : sub.state === "active" ? "Cambia piano su Stripe"
                    : `Attiva il piano ${PLAN_LABEL[stripePlan]}`}
                </button>
              ) : (
                <button type="button" className="wl-btn wl-btn--primary"
                        disabled={alreadyRequested || sendingReq}
                        onClick={requestModule} data-testid="upgrade-request-btn">
                  {alreadyRequested ? "Richiesta già inviata" : sendingReq ? "Invio…" : "Richiedi l'attivazione"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
